/* eslint-disable react/no-danger */
/**
 * AgentAutocomplete
 * 
 * @Author: Focci
 * @Date: 2023-07-20 14:40:54
 * @Last Modified by: Focci
 * @Last Modified time: 2023-07-20 14:40:54
 */

'use client'

import PropTypes from 'prop-types'
import clsx from 'clsx'
import useAbortController from '@hook/useAbortController'
import useProgressRouter from '@hook/useProgressRouter'
import Autocomplete from '@page/common/filter/Autocomplete'
import { Button } from '@comp/material'
import { fetchGet, fetchProxy } from '@lib/fetch/client'
import { debounce, forEach, isArray, last } from 'lodash-es'
import { useLocale, useTranslations } from 'next-intl'
import { useState, useCallback } from 'react'
import { clearHtmlTag } from '@lib/utils'
import * as agentRoute from '@lib/route/agent'
import ZoneSchool from '../combine/zone-school/index'
import { renderOption } from '../lib'

function getFilterOptions(data, ts, locale) {
  const result = []
  if(isArray(data.suburbs)) {
    forEach(data.suburbs, (item) => {
      result.push({
        typeName: ts('district'),
        type: 'district',
        label: item.label,
        value: item.id,
      })
    })
  }

  if(isArray(data.agents)) {
    forEach(data.agents, (item) => {
      const slug = `${item.firstName}-${item.lastName}-${item.id}`.toLocaleLowerCase()
      const url = agentRoute.detail(slug, locale)

      result.push({
        typeName: ts('agent'),
        type: 'link',
        label: item.name,
        value: item.id,
        slug,
        url
      })
    })
  }

  if(isArray(data.offices)) {
    forEach(data.offices, (item) => {
      const url = agentRoute.officeDetail(item.slug, locale)

      result.push({
        typeName: ts('office'),
        type: 'link',
        label: item.name,
        value: item.id,
        url
      })
    })
  }
  
  return result
}

export default function AgentAutocomplete({
  wraperClassName = 'w-160',
  value: v,
  onChange = () => {},
  autocompleteProps = {}
}) {
  const locale = useLocale()
  const router = useProgressRouter()
  const { getSignal } = useAbortController()

  const ts = useTranslations('search')
  const ta = useTranslations('agent')

  const [value, setValue] = useState(isArray(v) ? v : [])
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)

  // 输入关键字
  const handleInputChange = debounce((e) => {
    const { signal } = getSignal()

    e.target.value && fetchProxy(
      fetchGet('agent-promote', { key: e.target.value }, { signal })
        .then((data) => {
          setOptions(getFilterOptions(data, ts, locale))
        }),
      setLoading
    )
  }, 500)

  const handleChange = useCallback((e, data, reason) => {
    if(reason === 'selectOption') {
      // 选择地区过来的数据，关闭弹出框
      if(!e) { setOpen(false) } 

      const lastItem = last(data)
      if(lastItem?.type === 'link') {
        router.routerPush(lastItem.url)
      } else {
        onChange(lastItem)
      }
    } else if(reason === 'removeOption') {
      setValue(data)
    }
  }, [onChange, router])

  const renderOptionCallback = useCallback(
    (props, option) => renderOption(props, option), 
    []
  )

  return (
    <div className={clsx(['max-w-full flex', wraperClassName])}>
      <Autocomplete
        multiple
        freeSolo
        value={value}
        wraperClassName="focus-within:border-primary"
        loading={loading}
        options={options}
        onInputChange={handleInputChange}
        placeholder={ta('findAgentPlaceholder')}
        renderOption={renderOptionCallback}
        groupBy={(option) => option.typeName}
        getOptionLabel={(option) => clearHtmlTag(option.label)}
        onChange={handleChange}
        suffix={
          <div className="ml-1">
            <Button 
              className="!inline-flex"
              variant="outlined"
              onClick={() => setOpen(true)}
            >
              {ts('district')}
            </Button>
          </div>
        }
        {...autocompleteProps}
      />
      {open && (
        <ZoneSchool
          single
          module="district"
          open={open}
          onClose={() => setOpen(false)}
          onSubmit={({ data }) => handleChange(null, data, 'selectOption')}
        />
      )}
    </div>
  )
}

AgentAutocomplete.propTypes = {
  onSearchButtonClick: PropTypes.func,
  wraperClassName: PropTypes.string,
  searchButtonProps: PropTypes.object,
  onChange: PropTypes.func,
  autocompleteProps: PropTypes.object,
}
